import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import './VirtualizedList.scss';

const defaultKeyExtractor = (item, index) => item?.messageId || item?.id || `item-${index}`;

// --- EXTRACTED HELPER FUNCTIONS (Top Level) ---

const calculateScrollPosition = (offsetToRangeStart, rangeHeight, totalHeight, containerHeight, block, maxScroll) => {
    let scrollPosition;

    if (block === 'center') {
        const centerOfRange = offsetToRangeStart + rangeHeight / 2;
        scrollPosition = centerOfRange - containerHeight / 2;
        if (isNaN(scrollPosition) || scrollPosition < 0) {
            scrollPosition = offsetToRangeStart + rangeHeight / 2 - containerHeight / 2;
        }
    } else if (block === 'start') {
        scrollPosition = offsetToRangeStart;
    } else if (block === 'end') {
        scrollPosition = offsetToRangeStart + rangeHeight - containerHeight;
    } else {
        scrollPosition = offsetToRangeStart + rangeHeight / 2 - containerHeight / 2;
    }

    // Ensure scrollPosition is within valid bounds
    scrollPosition = Math.max(0, Math.min(scrollPosition, maxScroll));

    // Safety check: If scrollPosition is way too small or too large, use offsetToRangeStart
    if (offsetToRangeStart > 100) {
        if (
            scrollPosition < offsetToRangeStart * 0.3 ||
            scrollPosition > offsetToRangeStart + rangeHeight + containerHeight
        ) {
            scrollPosition = Math.max(0, Math.min(offsetToRangeStart, maxScroll));
        }
    }

    // Final scroll position
    let finalScrollPosition = Math.max(0, Math.min(scrollPosition, totalHeight - containerHeight));

    // Double-check: if finalScrollPosition is suspiciously small, use offsetToRangeStart
    if (offsetToRangeStart > 500 && finalScrollPosition < offsetToRangeStart * 0.3) {
        finalScrollPosition = Math.max(0, Math.min(offsetToRangeStart, totalHeight - containerHeight));
    }

    return finalScrollPosition;
};

const scrollElementIntoViewIfNeeded = (container, selector, smooth) => {
    const targetElement = container.querySelector(selector);
    if (targetElement) {
        const elementRect = targetElement.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const isVisible = elementRect.top >= containerRect.top && elementRect.bottom <= containerRect.bottom;

        if (!isVisible) {
            targetElement.scrollIntoView({
                behavior: smooth ? 'smooth' : 'auto',
                block: 'center',
            });
        }
    }
};

const verifyScrollPosition = (container, finalScrollPosition) => {
    const checkScrollTop = container.scrollTop;
    if (Math.abs(checkScrollTop - finalScrollPosition) > 10) {
        container.scrollTop = finalScrollPosition;
    }
};

const scheduleScrollElementIntoView = (container, selector, smooth) => {
    setTimeout(() => {
        scrollElementIntoViewIfNeeded(container, selector, smooth);
    }, 50);
};

const verifyAndFixScroll = (container, finalScrollPosition, selector, smooth) => {
    setTimeout(() => {
        verifyScrollPosition(container, finalScrollPosition);
        // DOM-based fallback: If scroll still didn't work, find the element and scroll to it directly
        scheduleScrollElementIntoView(container, selector, smooth);
    }, 10);
};

const verifyElementVisibility = (container, targetElement) => {
    if (!targetElement) {
        return;
    }

    const checkRect = targetElement.getBoundingClientRect();
    const checkContainerRect = container.getBoundingClientRect();
    const stillNotVisible = checkRect.top < checkContainerRect.top || checkRect.bottom > checkContainerRect.bottom;

    if (stillNotVisible) {
        targetElement.scrollIntoView({
            behavior: 'auto',
            block: 'center',
        });
    }
};

const scheduleElementVisibilityCheck = (container, targetElement) => {
    setTimeout(() => {
        verifyElementVisibility(container, targetElement);
    }, 50);
};

const fixScrollPositionIfNeeded = (container, targetElement) => {
    const actualScrollTop = container.scrollTop;
    const targetElementRect = targetElement?.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Check if scroll position is wrong and target element is not visible
    const spacerTopEl = container.querySelector('.virtualized-spacer');
    const spacerTopHeight = spacerTopEl?.offsetHeight || 0;
    const isScrollPositionWrong = actualScrollTop < 100 && spacerTopHeight > 1000;
    const isElementNotVisible =
        targetElementRect &&
        (targetElementRect.top < containerRect.top || targetElementRect.bottom > containerRect.bottom);

    // Fix if scroll position is wrong or element is not visible
    if (isScrollPositionWrong || isElementNotVisible) {
        const correctScrollPosition = Math.max(
            0,
            Math.min(spacerTopHeight, container.scrollHeight - container.clientHeight),
        );
        container.scrollTop = correctScrollPosition;

        // If still not visible, use scrollIntoView
        scheduleElementVisibilityCheck(container, targetElement);
    }
};

const checkAndFixScrollPosition = (container, selector, smooth, onComplete) => {
    setTimeout(() => {
        const targetElement = container.querySelector(selector);
        fixScrollPositionIfNeeded(container, targetElement);

        if (onComplete) {
            onComplete();
        }
    }, 50);
};

const waitForAnimationFrame = () => {
    return new Promise(resolve => {
        requestAnimationFrame(resolve);
    });
};

const waitForTimeout = ms => {
    return new Promise(resolve => {
        setTimeout(resolve, ms);
    });
};

const applyMessageHighlight = (container, messageId) => {
    const messageElement = container.querySelector(`[data-message-id="${messageId}"]`);
    if (messageElement) {
        messageElement.classList.add('search-highlight-active');
        setTimeout(() => {
            messageElement.classList.remove('search-highlight-active');
        }, 2000);
    }
};

// --- VIRTUALIZED BASE COMPONENT ---

const VirtualizedBase = (props, ref) => {
    // NOSONAR
    const {
        items = [],
        renderItem,
        containerRef,
        itemHeight = 100,
        overscan = 3,
        getItemHeight,
        keyExtractor = defaultKeyExtractor,
        onScroll,
    } = props;
    const [visibleRange, setVisibleRange] = useState({ start: 0, end: Math.min(20, items.length) });
    const itemHeightsRef = useRef(new Map());
    const containerHeightRef = useRef(0);
    const scrollTopRef = useRef(0);
    const isProgrammaticScrollRef = useRef(false);
    const visibleRangeRef = useRef(visibleRange);
    const targetIndexRef = useRef(null);
    const isRestoringScrollRef = useRef(false);
    const recalculationTimeoutRef = useRef(null);
    const offsetTopRef = useRef(0);
    const offsetBottomRef = useRef(0);
    const lastOffsetCalculationRef = useRef({ start: -1, end: -1, itemsLength: 0 });

    // Keep ref in sync with state
    useEffect(() => {
        visibleRangeRef.current = visibleRange;
    }, [visibleRange]);

    const calculateVisibleRange = useCallback(
        (force = false) => {
            const container = containerRef?.current;
            if (items.length === 0) {
                visibleRangeRef.current = { start: 0, end: 0 };
                setVisibleRange({ start: 0, end: 0 });
                return;
            }

            // Special handling for small item counts (1-5 items): always show all items
            // This prevents calculation issues when there are very few items
            if (items.length <= 5) {
                visibleRangeRef.current = { start: 0, end: items.length };
                setVisibleRange({ start: 0, end: items.length });
                return;
            }

            // If container is not ready, set a default range to show items
            if (!container) {
                const defaultEnd = Math.min(items.length, 20);
                visibleRangeRef.current = { start: 0, end: defaultEnd };
                setVisibleRange({ start: 0, end: defaultEnd });
                return;
            }

            if ((isProgrammaticScrollRef.current || isRestoringScrollRef.current) && !force) {
                return;
            }

            const containerHeight = container.clientHeight;
            containerHeightRef.current = containerHeight;

            // If container height is 0 or very small, show all items
            if (containerHeight <= 0) {
                const defaultEnd = Math.min(items.length, 20);
                visibleRangeRef.current = { start: 0, end: defaultEnd };
                setVisibleRange({ start: 0, end: defaultEnd });
                return;
            }

            const rawScrollTop = container.scrollTop;
            const scrollTop = rawScrollTop >= 0 ? rawScrollTop : Math.abs(rawScrollTop);
            scrollTopRef.current = scrollTop;

            const visibleTop = scrollTop;
            const visibleBottom = scrollTop + containerHeight;

            let start = 0;
            let end = items.length;
            let currentTop = 0;

            for (let i = 0; i < items.length; i++) {
                const height = itemHeightsRef.current.get(i) || getItemHeight?.(items[i], i) || itemHeight;
                if (currentTop + height > visibleTop) {
                    start = Math.max(0, i - overscan);
                    break;
                }
                currentTop += height;
            }

            currentTop = 0;
            for (let i = 0; i < items.length; i++) {
                const height = itemHeightsRef.current.get(i) || getItemHeight?.(items[i], i) || itemHeight;
                currentTop += height;
                if (currentTop >= visibleBottom) {
                    end = Math.min(items.length, i + 1 + overscan);
                    break;
                }
            }

            // Ensure we have a valid range
            if (start >= end || end === 0) {
                start = 0;
                end = Math.min(items.length, Math.max(20, items.length));
            }

            const previousRange = visibleRangeRef.current;
            const rangeChanged = previousRange.start !== start || previousRange.end !== end;

            if (!rangeChanged && !force) {
                return;
            }

            const wasProgrammaticScroll = targetIndexRef.current !== null || isProgrammaticScrollRef.current;
            if (
                scrollTop === 0 &&
                previousRange.end > end &&
                previousRange.start <= start &&
                !wasProgrammaticScroll &&
                !force
            ) {
                return;
            }

            visibleRangeRef.current = { start, end };
            setVisibleRange({ start, end });
        },
        [items, itemHeight, getItemHeight, overscan, containerRef],
    );

    useEffect(() => {
        const container = containerRef?.current;
        if (!container) return;

        calculateVisibleRange();

        let ticking = false;
        const handleScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    calculateVisibleRange();
                    if (onScroll) {
                        onScroll();
                    }
                    ticking = false;
                });
                ticking = true;
            }
        };

        container.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', calculateVisibleRange);

        return () => {
            container.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', calculateVisibleRange);
        };
    }, [calculateVisibleRange, containerRef, onScroll]);

    useEffect(() => {
        if (recalculationTimeoutRef.current) {
            clearTimeout(recalculationTimeoutRef.current);
        }

        if (isRestoringScrollRef.current || isProgrammaticScrollRef.current) {
            return;
        }

        recalculationTimeoutRef.current = setTimeout(() => {
            if (!isRestoringScrollRef.current && !isProgrammaticScrollRef.current) {
                calculateVisibleRange();
            }
        }, 100);
    }, [items.length, calculateVisibleRange]);

    useEffect(() => {
        return () => {
            if (recalculationTimeoutRef.current) {
                clearTimeout(recalculationTimeoutRef.current);
            }
        };
    }, []);

    const calculateScrollMetrics = useCallback(
        (newStart, newEnd) => {
            let offsetToRangeStart = 0;
            for (let i = 0; i < newStart; i++) {
                const height = itemHeightsRef.current.get(i) || getItemHeight?.(items[i], i) || itemHeight;
                offsetToRangeStart += height;
            }

            let rangeHeight = 0;
            for (let i = newStart; i < newEnd; i++) {
                const height = itemHeightsRef.current.get(i) || getItemHeight?.(items[i], i) || itemHeight;
                rangeHeight += height;
            }

            return { offsetToRangeStart, rangeHeight };
        },
        [items, itemHeight, getItemHeight],
    );

    const performScroll = useCallback(
        (container, newStart, newEnd, block, smooth, selector, onScrollComplete) => {
            const totalHeight = container.scrollHeight;
            const containerHeight = container.clientHeight;

            const { offsetToRangeStart, rangeHeight } = calculateScrollMetrics(newStart, newEnd);

            const maxScroll = Math.max(0, totalHeight - containerHeight);
            const finalScrollPosition = calculateScrollPosition(
                offsetToRangeStart,
                rangeHeight,
                totalHeight,
                containerHeight,
                block,
                maxScroll,
            );

            container.scrollTo({
                top: finalScrollPosition,
                behavior: smooth ? 'smooth' : 'auto',
            });
            container.scrollTop = finalScrollPosition;

            verifyAndFixScroll(container, finalScrollPosition, selector, smooth);

            checkAndFixScrollPosition(container, selector, smooth, onScrollComplete);
        },
        [calculateScrollMetrics],
    );

    const executeScrollAfterRender = useCallback(
        async (container, newStart, newEnd, block, smooth, selector, onScrollComplete) => {
            await waitForTimeout(0);
            await waitForAnimationFrame();
            await waitForAnimationFrame();
            await waitForTimeout(100);
            performScroll(container, newStart, newEnd, block, smooth, selector, onScrollComplete);
        },
        [performScroll],
    );

    const resetScrollFlags = useCallback(() => {
        isProgrammaticScrollRef.current = false;
        isRestoringScrollRef.current = false;
        targetIndexRef.current = null;
        requestAnimationFrame(() => {
            calculateVisibleRange(true);
        });
    }, [calculateVisibleRange]);

    // --- Imperative Handles Extracted to Callbacks (Fixes Nesting Error) ---

    const jumpToBottom = useCallback(
        (options = {}) => {
            const container = containerRef?.current;
            if (!container) return;

            const { smooth = false } = options;

            container.scrollTo({
                top: 0,
                behavior: smooth ? 'smooth' : 'auto',
            });

            setTimeout(
                () => {
                    calculateVisibleRange();
                },
                smooth ? 300 : 0,
            );
        },
        [containerRef, calculateVisibleRange],
    );

    const handleScrollToIndex = useCallback(
        (index, options = {}) => {
            const container = containerRef?.current;
            if (!container || index < 0 || index >= items.length) return;

            const { smooth = false } = options;

            let offset = 0;
            for (let i = 0; i < index; i++) {
                const height = itemHeightsRef.current.get(i) || getItemHeight?.(items[i], i) || itemHeight;
                offset += height;
            }

            const totalHeight = container.scrollHeight;
            const scrollPosition = totalHeight - offset - container.clientHeight;

            container.scrollTo({
                top: Math.max(0, scrollPosition),
                behavior: smooth ? 'smooth' : 'auto',
            });

            setTimeout(
                () => {
                    calculateVisibleRange();
                },
                smooth ? 300 : 0,
            );
        },
        [items, itemHeight, getItemHeight, containerRef, calculateVisibleRange],
    );

    const createUnreadScrollCompleteHandler = useCallback(
        smooth => {
            return () => {
                const delay = smooth ? 1000 : 500;
                setTimeout(() => {
                    resetScrollFlags();
                }, delay);
            };
        },
        [resetScrollFlags],
    );

    const handleScrollToUnreadAnchor = useCallback(
        (options = {}) => {
            const container = containerRef?.current;
            if (!container) return;

            const { smooth = false, block = 'center' } = options;

            const unreadIndex = items.findIndex(item => item.type === 'unread-separator');

            if (unreadIndex === -1) {
                return;
            }

            targetIndexRef.current = unreadIndex;
            const newStart = Math.max(0, unreadIndex - 20);
            const newEnd = Math.min(items.length, unreadIndex + 21);

            setVisibleRange({ start: newStart, end: newEnd });
            isProgrammaticScrollRef.current = true;

            const onScrollComplete = createUnreadScrollCompleteHandler(smooth);

            executeScrollAfterRender(
                container,
                newStart,
                newEnd,
                block,
                smooth,
                '[data-unread-anchor="true"]',
                onScrollComplete,
            );

            return unreadIndex;
        },
        [items, containerRef, executeScrollAfterRender, createUnreadScrollCompleteHandler],
    );

    const handleResetAfterDelay = useCallback(
        smooth => {
            const resetDelay = smooth ? 1000 : 500;
            setTimeout(() => {
                resetScrollFlags();
            }, resetDelay);
        },
        [resetScrollFlags],
    );

    const handleHighlightAndReset = useCallback(
        (container, messageId, highlight, smooth) => {
            const highlightDelay = smooth ? 300 : 100;
            setTimeout(() => {
                if (highlight) {
                    applyMessageHighlight(container, messageId);
                }
                handleResetAfterDelay(smooth);
            }, highlightDelay);
        },
        [handleResetAfterDelay],
    );

    const createMessageScrollCompleteHandler = useCallback(
        (container, messageId, highlight, smooth) => {
            return () => {
                handleHighlightAndReset(container, messageId, highlight, smooth);
            };
        },
        [handleHighlightAndReset],
    );

    const handleScrollToMessage = useCallback(
        (messageId, options = {}) => {
            const container = containerRef?.current;
            if (!container || !messageId) return;

            const { smooth = false, block = 'center', highlight = false } = options;

            const messageIndex = items.findIndex(item => item.messageId === messageId || item.id === messageId);

            if (messageIndex === -1) {
                return;
            }

            targetIndexRef.current = messageIndex;
            const newStart = Math.max(0, messageIndex - 20);
            const newEnd = Math.min(items.length, messageIndex + 21);

            setVisibleRange({ start: newStart, end: newEnd });

            isProgrammaticScrollRef.current = true;
            if (block === 'bottom' || block === 'end') {
                isRestoringScrollRef.current = true;
            }

            const onScrollComplete = createMessageScrollCompleteHandler(container, messageId, highlight, smooth);

            executeScrollAfterRender(
                container,
                newStart,
                newEnd,
                block,
                smooth,
                `[data-message-id="${messageId}"]`,
                onScrollComplete,
            );

            return messageIndex;
        },
        [items, containerRef, executeScrollAfterRender, createMessageScrollCompleteHandler],
    );

    useImperativeHandle(
        // NOSONAR
        ref,
        () => ({
            // NOSONAR
            jumpToBottom,
            getScrollContainer: () => containerRef?.current || null,
            scrollToIndex: handleScrollToIndex,
            scrollToUnreadAnchor: handleScrollToUnreadAnchor,
            scrollToMessage: handleScrollToMessage,
        }),
        [jumpToBottom, containerRef, handleScrollToIndex, handleScrollToUnreadAnchor, handleScrollToMessage],
    );

    // --- Offset Calculations and Measure Item (Kept as is) ---
    const offsetTop = useMemo(() => {
        if (isRestoringScrollRef.current) {
            return offsetTopRef.current;
        }

        let start;
        if (targetIndexRef.current !== null) {
            start = Math.max(0, targetIndexRef.current - 20);
        } else {
            start = visibleRange.start;
        }

        const needsRecalculation =
            lastOffsetCalculationRef.current.start !== start ||
            lastOffsetCalculationRef.current.itemsLength !== items.length;

        if (!needsRecalculation && start > 0) {
            return offsetTopRef.current;
        }

        if (start === 0) {
            offsetTopRef.current = 0;
            lastOffsetCalculationRef.current.start = start;
            lastOffsetCalculationRef.current.itemsLength = items.length;
            return 0;
        }

        let offset = 0;
        for (let i = 0; i < start; i++) {
            const height = itemHeightsRef.current.get(i) || getItemHeight?.(items[i], i) || itemHeight;
            offset += height;
        }

        offsetTopRef.current = offset;
        lastOffsetCalculationRef.current.start = start;
        lastOffsetCalculationRef.current.itemsLength = items.length;
        return offset;
    }, [visibleRange.start, items, itemHeight, getItemHeight]);

    const offsetBottom = useMemo(() => {
        if (isRestoringScrollRef.current) {
            return offsetBottomRef.current;
        }

        let end;
        if (targetIndexRef.current !== null) {
            end = Math.min(items.length, targetIndexRef.current + 21);
        } else {
            end = visibleRange.end;
        }

        const needsRecalculation =
            lastOffsetCalculationRef.current.end !== end ||
            lastOffsetCalculationRef.current.itemsLength !== items.length;

        if (!needsRecalculation && end < items.length) {
            return offsetBottomRef.current;
        }

        if (end >= items.length) {
            offsetBottomRef.current = 0;
            lastOffsetCalculationRef.current.end = end;
            lastOffsetCalculationRef.current.itemsLength = items.length;
            return 0;
        }

        let offset = 0;
        for (let i = end; i < items.length; i++) {
            const height = itemHeightsRef.current.get(i) || getItemHeight?.(items[i], i) || itemHeight;
            offset += height;
        }

        offsetBottomRef.current = offset;
        lastOffsetCalculationRef.current.end = end;
        lastOffsetCalculationRef.current.itemsLength = items.length;
        return offset;
    }, [visibleRange.end, items, itemHeight, getItemHeight]);

    const measureItem = useCallback(
        (index, height) => {
            if (height && height > 0) {
                const current = itemHeightsRef.current.get(index);
                if (current !== height) {
                    itemHeightsRef.current.set(index, height);
                    if (
                        Math.abs(current - height) > 20 &&
                        !isRestoringScrollRef.current &&
                        !isProgrammaticScrollRef.current
                    ) {
                        if (recalculationTimeoutRef.current) {
                            clearTimeout(recalculationTimeoutRef.current);
                        }
                        recalculationTimeoutRef.current = setTimeout(() => {
                            if (!isRestoringScrollRef.current && !isProgrammaticScrollRef.current) {
                                calculateVisibleRange();
                            }
                        }, 150);
                    }
                }
            }
        },
        [calculateVisibleRange],
    );

    const visibleItems = useMemo(() => {
        const result = [];

        if (items.length === 0) {
            return result;
        }

        // Special handling for small item counts: always show all items
        if (items.length <= 15) {
            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                const key = keyExtractor ? keyExtractor(item, i) : defaultKeyExtractor(item, i);
                result.push(
                    <div key={key} data-index={i} className="virtualized-item">
                        {renderItem(item, i, measureItem)}
                    </div>,
                );
            }
            return result;
        }

        let start, end;
        if (targetIndexRef.current !== null) {
            const targetIndex = targetIndexRef.current;
            start = Math.max(0, targetIndex - 20);
            end = Math.min(items.length, targetIndex + 21);
        } else {
            start = visibleRange.start;
            end = visibleRange.end;
        }

        // Fallback: If range is invalid or empty, show all items (or at least first few)
        if (start >= end || end === 0) {
            start = 0;
            end = Math.min(items.length, Math.max(20, items.length));
        }

        // Ensure start and end are within valid bounds
        start = Math.max(0, Math.min(start, items.length));
        end = Math.max(start, Math.min(end, items.length));

        for (let i = start; i < end; i++) {
            if (i >= items.length) break;
            const item = items[i];
            const key = keyExtractor ? keyExtractor(item, i) : defaultKeyExtractor(item, i);
            result.push(
                <div key={key} data-index={i} className="virtualized-item">
                    {renderItem(item, i, measureItem)}
                </div>,
            );
        }
        return result;
    }, [items, visibleRange, renderItem, measureItem, keyExtractor]);

    if (items.length === 0) {
        return null;
    }

    return (
        <>
            {offsetTop > 0 && <div className="virtualized-spacer" style={{ height: offsetTop }} />}

            {visibleItems}

            {offsetBottom > 0 && <div className="virtualized-spacer" style={{ height: offsetBottom }} />}
        </>
    );
};

const VirtualizedList = forwardRef(VirtualizedBase);

VirtualizedBase.propTypes = {
    items: PropTypes.array,
    renderItem: PropTypes.func.isRequired,
    containerRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({
            current: PropTypes.any,
        }),
    ]),
    itemHeight: PropTypes.number,
    overscan: PropTypes.number,
    getItemHeight: PropTypes.func,
    keyExtractor: PropTypes.func,
    onScroll: PropTypes.func,
};

export default VirtualizedList;
